"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useRun } from "@/components/tools/reference-tools/use-run";
import { StyleRowCard } from "@/components/tools/style-converter/row";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import { crossrefQueryUrl } from "@/lib/tool-kit/reference-tools/doi-finder";
import { fetchJson, runPool } from "@/lib/tool-kit/reference-tools/network";
import {
  assembleList,
  type ConvertResult,
  convertedTexts,
  convertRows,
  MAX_REFERENCES,
  planRows,
  type RefRow,
  rowDoi,
  STYLE_OPTIONS,
  type StyleId,
  styleOption,
  withMatch,
} from "@/lib/tool-kit/style-converter/convert";

/** English strings. A translated content file overrides these through `ui`. */
export const CITATION_STYLE_CONVERTER_STRINGS = {
  inputLabel:
    "Your reference list, in any style — one reference per line, or separated by blank lines",
  inputPlaceholder:
    "Hirsch, J. E. (2005). An index to quantify an individual's scientific research output. Proceedings of the National Academy of Sciences, 102(46), 16569–16572.\nEgghe, L. (2006). Theory and practise of the g-index. Scientometrics, 69(1), 131–152.",
  inputHint:
    "Up to {max} references per run. Each is searched on Crossref, one per second, from your browser.",
  styleLabel: "Convert to",
  styles: {
    apa: "APA 7",
    mla: "MLA 9",
    chicagoAuthorDate: "Chicago 18, author-date",
    chicagoNotes: "Chicago 18, notes and bibliography",
    harvard: "Harvard",
    ieee: "IEEE",
    vancouver: "Vancouver",
    ama: "AMA",
    asa: "ASA",
    nature: "Nature",
  },
  sortLabel: "Sort alphabetically by author",
  numberedNote:
    "{style} numbers its references, so the list keeps the order you pasted.",
  find: "Find matches",
  finding: "Searching {done} of {total}…",
  convert: "Convert to {style}",
  converting: "Fetching {done} of {total}…",
  cancel: "Stop",
  privacy:
    "Finding matches sends each reference's text (without list numbers and links) to Crossref, straight from your browser. Converting sends only the DOIs you confirmed to CitationEasy's server, which fetches each record from Crossref and formats it. Your list itself is not stored.",
  emptyPrompt: "Paste a reference list and press Find matches.",
  unexpected: "Something went wrong. Please try again.",
  matchesHeading: "Reference by reference",
  matchesHint:
    "Confident matches are ticked. Check each Crossref record against your reference, tick the ones that are the same work, then convert.",
  states: {
    confident: "Confident match",
    uncertain: "Possible match",
    none: "No match",
    given: "DOI in your reference",
    short: "Too short to search",
    over: "Over the limit",
    failed: "Search failed",
    queued: "Waiting",
    converted: "Converted",
    unconverted: "Not converted",
  },
  yourReference: "Your reference",
  crossrefRecord: "Crossref record",
  accept: "Convert from this record",
  useGivenDoi: "Convert from the DOI in this reference ({doi})",
  etAl: "et al.",
  checks: {
    title: "Title words found in your reference: {share}",
    authorYes: "First author ({name}) matches",
    authorNo: "First author ({name}) is not in your reference",
    authorNone: "Crossref lists no author",
    yearExact: "Year matches ({year})",
    yearNear: "Year is one off ({year}) — often online-first vs print",
    yearNo: "Year differs ({year})",
    yearUnknown: "No year to compare",
  },
  notes: {
    none: "No Crossref record matches closely. Many books, reports, theses, web pages and older works have no DOI.",
    short: "Fewer than four words — not enough to search.",
    over: "Run the list again with the remaining references.",
  },
  failures: {
    "rate-limited":
      "Crossref is limiting requests. Wait a minute and try again.",
    unavailable: "Crossref did not answer properly. Try again shortly.",
    timeout: "Crossref took too long to answer. Try again.",
    network: "Crossref could not be reached — check your connection.",
    "not-found": "Crossref returned nothing for this reference.",
  },
  convertFailures: {
    "rate-limited":
      "The record service is limiting requests. Try again shortly.",
    unavailable:
      "The record could not be fetched right now. Try again shortly.",
    timeout: "The record took too long to arrive. Try again.",
    network: "The record could not be fetched — check your connection.",
    "not-found": "No record could be fetched for this DOI.",
    blocked: "The publisher's record could not be fetched for this DOI.",
    format: "The record was fetched but could not be formatted in this style.",
  },
  manualFlag: "Needs manual entry.",
  manualOpen: "Open the {generator}",
  generators: {
    journal: "Journal Article Citation Generator",
    book: "Book Citation Generator",
    chapter: "Book Chapter Citation Generator",
    website: "Website Citation Generator",
    general: "Citation Generator",
  },
  resultsHeading: "Your list in {style}",
  converted:
    "one:{count} reference converted|other:{count} references converted",
  leftOver:
    "one:{count} reference needs manual entry|other:{count} references need manual entry",
  copy: "Copy list",
  copied: "Copied!",
  download: "Download .txt",
  outputNote:
    "Each reference is formatted from the Crossref record you confirmed, not from your original wording. Check names, capitalisation, page ranges and any source type the record gets wrong.",
} as const;

export type CitationStyleConverterStrings =
  typeof CITATION_STYLE_CONVERTER_STRINGS;

/** Crossref's public pool allows one list query per second. */
const QUERY_INTERVAL_MS = 1100;

export default function CitationStyleConverterWidget() {
  const strings = useToolUi(CITATION_STYLE_CONVERTER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [styleId, setStyleId] = useState<StyleId>("apa");
  const [sort, setSort] = useState(true);
  const [rows, setRows] = useState<RefRow[]>([]);
  const [results, setResults] = useState<Map<number, ConvertResult> | null>(
    null
  );
  const [failed, setFailed] = useState(false);
  const search = useRun();
  const convert = useRun();
  const style = styleOption(styleId);
  const styleName = strings.styles[styleId];
  const confirmed = rows.filter((row) => rowDoi(row) !== null).length;

  async function handleFind() {
    const planned = planRows(text);
    setRows(planned);
    setResults(null);
    setFailed(false);
    const queue = planned.filter((row) => row.state === "queued");
    const controller = search.start(queue.length);
    const update = (next: RefRow) => {
      if (search.isCurrent(controller)) {
        setRows((current) =>
          current.map((row) => (row.id === next.id ? next : row))
        );
      }
    };
    try {
      await runPool(
        queue,
        async (row) => {
          const response = await fetchJson<unknown>(
            crossrefQueryUrl(row.reference),
            { signal: controller.signal, retries: 1 }
          );
          update(
            response.ok
              ? withMatch(row, response.value)
              : { ...row, state: "failed", reason: response.reason }
          );
        },
        {
          concurrency: 1,
          minIntervalMs: QUERY_INTERVAL_MS,
          signal: controller.signal,
          onProgress: (done) => search.advance(controller, done, queue.length),
        }
      );
    } catch {
      if (search.isCurrent(controller)) {
        setFailed(true);
      }
    } finally {
      search.finish(controller);
    }
  }

  async function handleConvert() {
    setFailed(false);
    const controller = convert.start(confirmed);
    try {
      const next = await convertRows(rows, style, {
        signal: controller.signal,
        onProgress: (done, total) => convert.advance(controller, done, total),
      });
      if (convert.isCurrent(controller)) {
        setResults(next);
      }
    } catch {
      if (convert.isCurrent(controller)) {
        setFailed(true);
      }
    } finally {
      convert.finish(controller);
    }
  }

  function toggle(id: number, accepted: boolean) {
    setResults(null);
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, accepted } : row))
    );
  }

  function changeStyle(next: StyleId) {
    setResults(null);
    setStyleId(next);
  }

  const searched = rows.some((row) => row.state !== "queued");
  const list = useMemo(
    () =>
      results
        ? assembleList(convertedTexts(rows, results), style, sort).join("\n\n")
        : "",
    [results, rows, style, sort]
  );
  const done = results ? convertedTexts(rows, results).length : 0;
  const manual = rows.filter(
    (row) => row.state !== "over" && !results?.get(row.id)?.ok
  ).length;
  const busy = search.running || convert.running;
  const label = convert.progress?.total
    ? t(strings.converting, {
        done: fmt(convert.progress.done),
        total: fmt(convert.progress.total),
      })
    : t(strings.convert, { style: styleName });

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={t(strings.inputHint, { max: fmt(MAX_REFERENCES) })}
        id="csc-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        prose
        rows={8}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="csc-style"
          label={strings.styleLabel}
          onChange={changeStyle}
          options={STYLE_OPTIONS.map((option) => ({
            value: option.id,
            label: strings.styles[option.id],
          }))}
          value={styleId}
        />
        <div className="flex items-end pb-2">
          {style.numbered ? (
            <p className={HINT}>
              {t(strings.numberedNote, { style: styleName })}
            </p>
          ) : (
            <Checkbox
              checked={sort}
              label={strings.sortLabel}
              onChange={setSort}
            />
          )}
        </div>
      </div>
      <p className={HINT}>{strings.privacy}</p>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_PRIMARY}
          disabled={busy || text.trim() === ""}
          onClick={handleFind}
          type="button"
        >
          {search.progress
            ? t(strings.finding, {
                done: fmt(search.progress.done),
                total: fmt(search.progress.total),
              })
            : strings.find}
        </button>
        {searched ? (
          <button
            className={BUTTON_PRIMARY}
            disabled={busy || confirmed === 0}
            onClick={handleConvert}
            type="button"
          >
            {label}
          </button>
        ) : null}
        {busy ? (
          <button
            className={BUTTON_SECONDARY}
            onClick={search.running ? search.cancel : convert.cancel}
            type="button"
          >
            {strings.cancel}
          </button>
        ) : null}
      </div>
      <div aria-live="polite" className="space-y-6">
        {failed ? <ErrorNotice>{strings.unexpected}</ErrorNotice> : null}
        {rows.length === 0 ? <Notice>{strings.emptyPrompt}</Notice> : null}
        {results ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className={WIDGET_H2}>
                  {t(strings.resultsHeading, { style: styleName })}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {tPlural(strings.converted, locale, done, {
                    count: fmt(done),
                  })}
                  {manual > 0
                    ? ` · ${tPlural(strings.leftOver, locale, manual, { count: fmt(manual) })}`
                    : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copy}
                  text={list}
                />
                <button
                  className={BUTTON_SECONDARY}
                  onClick={() => downloadText("references.txt", list)}
                  type="button"
                >
                  {strings.download}
                </button>
              </div>
            </div>
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
              {list}
            </pre>
            <p className={HINT}>{strings.outputNote}</p>
          </div>
        ) : null}
        {rows.length > 0 ? (
          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.matchesHeading}</h3>
            {searched && !results ? (
              <p className={HINT}>{strings.matchesHint}</p>
            ) : null}
            <ol className="space-y-3">
              {rows.map((row) => (
                <StyleRowCard
                  key={row.id}
                  onToggle={toggle}
                  result={results?.get(row.id)}
                  row={row}
                  strings={strings}
                />
              ))}
            </ol>
          </div>
        ) : null}
      </div>
    </section>
  );
}
